import React from 'react';
import * as Yup from 'yup';
import { Box, Chip, styled, Typography } from '@mui/material';
import { IPublicAndPrivateSettings } from '@pepperhq/location-sdk';
import { availableLocales, LocaleCode, RegionCode, regionSetting } from '@pepperhq/regions';
import * as lookup from 'country-code-lookup';
import { getAllTimezones } from 'countries-and-timezones';

import { MuiCardForm } from 'lib/form/MuiCardForm';
import { isDefined } from 'lib/typeguards';
import { Option } from 'lib/types';
import { Row } from 'ui/Flex';
import { SettingDisplay } from 'lib/SettingDisplay';
import { SettingDisplayFile } from '../SettingDisplayFile';
import { SettingDisplaySwitch } from '../SettingDisplaySwitch';
import { SettingDisplayText } from '../SettingDisplayText';
import { useMediaCheck } from 'lib/hooks/useMediaCheck';
import { AutocompleteFormField } from 'lib/form/fields/AutocompleteFormField';
import { FormikValues } from 'formik';
import { SelectFormField } from 'lib/form/fields/SelectFormField';
import { TextFormField } from 'lib/form/fields/TextFormField';
import { ChipInputFormField } from 'lib/form/fields/ChipInputFormField';
import { FileInputFormField } from 'lib/form/fields/FileInputFormField';
import { NumberFormField } from 'lib/form/fields/NumberFormField';
import { SwitchFormField } from 'lib/form/fields/SwitchFormField';

const generalSettingsValidationSchema = Yup.object().shape({
    identityProviders: Yup.array().of(Yup.string()).min(1, 'Please select at least one identity provider.')
});

const PREFIX = 'GeneralSettings';

const classes = {
    settingsCard: `${PREFIX}-settingsCard`,
    displayClassName: `${PREFIX}-displayClassName`,
    identityChip: `${PREFIX}-identityChip`
};

const StyledMuiCardForm = styled(MuiCardForm)(({ theme }) => ({
    [`&.${classes.settingsCard}`]: {
        marginBottom: theme.spacing(2)
    },
    [`& .${classes.displayClassName}`]: {
        padding: theme.spacing(2)
    },
    [`& .${classes.identityChip}`]: {
        marginRight: theme.spacing(0.5)
    }
}));

const identityProvidersOptions: Option[] = [
    { label: 'Phone', value: 'phone' },
    { label: 'Email', value: 'email' }
];

const localeOptions = (() => {
    const result: { label: string; value: LocaleCode }[] = [];
    availableLocales.forEach(locale => result.push({ label: locale, value: locale }));
    return result;
})();

const timezoneOptions = Object.entries(getAllTimezones()).map(tz => ({ label: tz[0], value: tz[0] }));

const supportedCountryCallingCodeOptions: Option[] = Object.entries(regionSetting).map(([code, setting]) => ({
    label: lookup.byIso(code)?.country ?? 'United Kingdom',
    value: setting.phoneNumberCode
}));

const regionsToDisplay = (() => {
    const result: { label: string; value: string }[] = [];
    for (const region in RegionCode) {
        result.push({
            label: lookup.byIso(region)?.country ?? 'United Kingdom',
            value: region
        });
    }
    return result;
})();

const filePickerAttributes = { type: 'file', accept: '.md' };

enum EUserBarcodeFormat {
    PDF417 = 'PDF417',
    QR = 'QR'
}

const userBarcodeFormatLabels: Record<EUserBarcodeFormat, string> = {
    [EUserBarcodeFormat.PDF417]: 'PDF417 Barcode',
    [EUserBarcodeFormat.QR]: 'QR Code'
};

export enum EBarcodePrefix {
    UNSET = 'UNSET',
    DEFAULT = 'DEFAULT',
    EMPTY = 'EMPTY',
    CUSTOM = 'CUSTOM'
}

const barcodePrefixTypeOptions: Option[] = [
    { label: 'Unset', value: EBarcodePrefix.UNSET },
    { label: 'Default (p377)', value: EBarcodePrefix.DEFAULT },
    { label: 'Empty', value: EBarcodePrefix.EMPTY },
    { label: 'Custom', value: EBarcodePrefix.CUSTOM }
];

const getBarcodePrefixType = (prefix: string | null | undefined): EBarcodePrefix => {
    if (prefix === null || prefix === undefined) {
        return EBarcodePrefix.UNSET;
    }
    if (prefix === 'p377') {
        return EBarcodePrefix.DEFAULT;
    }
    if (prefix === '') {
        return EBarcodePrefix.EMPTY;
    }
    return EBarcodePrefix.CUSTOM;
};

export const getBarcodePrefixValue = (type: EBarcodePrefix) => {
    switch (type) {
        case EBarcodePrefix.UNSET:
            return null;
        case EBarcodePrefix.DEFAULT:
            return 'p377';
        case EBarcodePrefix.EMPTY:
            return '';
        case EBarcodePrefix.CUSTOM:
            return undefined;
        default:
            break;
    }
};

const getUserBarcodeFormatLabel = (provider: string | undefined): string => {
    if (provider === undefined) {
        return userBarcodeFormatLabels[EUserBarcodeFormat.PDF417];
    }

    return userBarcodeFormatLabels[provider as EUserBarcodeFormat];
};

const userBarcodeFormatOptions = [
    {
        label: userBarcodeFormatLabels[EUserBarcodeFormat.PDF417],
        value: EUserBarcodeFormat.PDF417
    },
    {
        label: userBarcodeFormatLabels[EUserBarcodeFormat.QR],
        value: EUserBarcodeFormat.QR
    }
];

enum EAppUserAuth {
    LEGACY = 'None (legacy)',
    PASSWORD = 'Password Only',
    VERIFY_PASSWORD = 'Two-Factor Authentication'
}

type IProps = {
    settings: IPublicAndPrivateSettings;
    tenantId: string;
    onSubmit: (values: Record<string, any>) => Promise<boolean>;
};

export const GeneralSettingsCard = ({ settings, tenantId, onSubmit }: IProps) => {
    const [faqMDSrc, faqMdLoading, _checkFaq, getFaqUrl] = useMediaCheck(
        `${import.meta.env.MEDIA_SERVICE_URL}/tenants/${tenantId}/app_assets/faq`,
        'faq'
    );
    const [aboutMDSrc, aboutMdLoading, _checkAbout, getAboutUrl] = useMediaCheck(
        `${import.meta.env.MEDIA_SERVICE_URL}/tenants/${tenantId}/app_assets/about`,
        'about'
    );
    const [privacyMDSrc, privacyMdLoading, _checkPrivacy, getPrivacyUrl] = useMediaCheck(
        `${import.meta.env.MEDIA_SERVICE_URL}/tenants/${tenantId}/app_assets/privacy`,
        'privacy'
    );
    const [termsMDSrc, termsMdLoading, _checkTerms, getTermsUrl] = useMediaCheck(
        `${import.meta.env.MEDIA_SERVICE_URL}/tenants/${tenantId}/app_assets/terms`,
        'terms'
    );

    const getAppUserAuthDisplayText = (setting: string | undefined): string => {
        switch (setting) {
            case 'PASSWORD':
                return EAppUserAuth.PASSWORD;
            case 'VERIFY_PASSWORD':
                return EAppUserAuth.VERIFY_PASSWORD;
            default:
                return EAppUserAuth.LEGACY;
        }
    };

    const renderSupportedCountryCodes = React.useCallback(() => {
        if (!settings?.phoneAuthEnabled) {
            return null;
        }
        const supportedCodesToShow = (settings?.phoneAuth?.supportedCountryCallingCodes ?? []).slice(0, 7);
        const hiddenCodesCount =
            settings?.phoneAuth?.supportedCountryCallingCodes?.length - supportedCodesToShow.length;
        return (
            <SettingDisplay label="Supported Country Calling Codes on Registration">
                <Row valign="center" wrap="wrap" flex={1}>
                    {supportedCodesToShow.map(code => (
                        <Chip key={code} label={code} color="primary" className={classes.identityChip} />
                    ))}
                    {!!hiddenCodesCount && (
                        <Typography variant="body2" key="helper-text">
                            + {hiddenCodesCount} more
                        </Typography>
                    )}
                </Row>
            </SettingDisplay>
        );
    }, [settings?.phoneAuth?.supportedCountryCallingCodes, settings?.phoneAuthEnabled]);

    const renderGlobalEdit = React.useCallback(
        (values: FormikValues) => (
            <Box display="flex" flexDirection="column">
                <SettingDisplay label="Region" buildtimeIOS>
                    <AutocompleteFormField name="region" options={regionsToDisplay} />
                </SettingDisplay>
                <SettingDisplay label="User Authentication">
                    <SelectFormField
                        name="appUserAuth"
                        options={[
                            { label: EAppUserAuth.LEGACY, value: 'LEGACY' },
                            { label: EAppUserAuth.PASSWORD, value: 'PASSWORD' },
                            { label: EAppUserAuth.VERIFY_PASSWORD, value: 'VERIFY_PASSWORD' }
                        ]}
                    />
                </SettingDisplay>
                <SettingDisplay label="Timezone">
                    <AutocompleteFormField name="timezone" options={timezoneOptions} />
                </SettingDisplay>
                <SettingDisplay label="Locale">
                    <AutocompleteFormField name="locale" options={localeOptions} />
                </SettingDisplay>
                <SettingDisplay label="Tax Label" buildtimeIOS>
                    <TextFormField name="taxLabel" />
                </SettingDisplay>
                <SettingDisplay label="Identity Providers">
                    <AutocompleteFormField
                        name="identityProviders"
                        options={identityProvidersOptions}
                        multiple
                    />
                </SettingDisplay>
                <SettingDisplay
                    label="Unique Device Verification"
                    description={'Should users only be able to sign up for a single account per device'}
                >
                    <SwitchFormField name="uniqueDeviceVerificationEnabled" />
                </SettingDisplay>
                {values.identityProviders.some((provider: string) => provider === 'phone') && (
                    <SettingDisplay label="Supported Country Calling Codes on Registration">
                        <ChipInputFormField
                            name="supportedCountryCallingCodes"
                            options={supportedCountryCallingCodeOptions}
                            multiple
                            isCreatable={false}
                        />
                    </SettingDisplay>
                )}
                <SettingDisplay
                    label="Age Request Enabled"
                    description="Should the App ask the user for their age on sign up?"
                >
                    <SwitchFormField name="ageRequestEnabled" />
                </SettingDisplay>
                <SettingDisplay
                    label="Signup Minimum Age"
                    description="What is the minimum age, if enabled, to sign up to the App?"
                >
                    <NumberFormField name="registrationMinimumAge" />
                </SettingDisplay>
                <SettingDisplay
                    label="Ask for Marketing on Registration"
                    description="Should the App ask the user for marketing preferences on sign up?"
                >
                    <SwitchFormField name="registrationShowMarketingOptIn" />
                </SettingDisplay>
                <SettingDisplay label="Offline Checkin">
                    <SwitchFormField name="offlineCheckinEnabled" />
                </SettingDisplay>
                <SettingDisplay label="User Barcode Format">
                    <SelectFormField name="userBarcodeFormat" options={userBarcodeFormatOptions} />
                </SettingDisplay>
                <SettingDisplay label="Barcode Prefix">
                    <Box display="flex" flexDirection="column" gap={1} width="100%">
                        <SelectFormField name="userBarcodePrefixType" options={barcodePrefixTypeOptions} />
                        {values.userBarcodePrefixType === EBarcodePrefix.CUSTOM && (
                            <TextFormField name="userBarcodePrefix" />
                        )}
                    </Box>
                </SettingDisplay>
                <SettingDisplay label="Additional Credentials">
                    <SwitchFormField name="secondaryCredentialsEnabled" />
                </SettingDisplay>
                <SettingDisplay label="App Store Ratings & Reviews Enabled">
                    <SwitchFormField name="inAppReviewPromptsEnabled" />
                </SettingDisplay>
                <SettingDisplay label="Show Pepper Copyright">
                    <SwitchFormField name="showCopyright" />
                </SettingDisplay>
                <SettingDisplay label="About Markdown">
                    <FileInputFormField
                        name="aboutMD"
                        attributes={filePickerAttributes}
                        path={aboutMDSrc}
                        isLoading={aboutMdLoading}
                    />
                </SettingDisplay>
                <SettingDisplay label="FAQ Markdown">
                    <FileInputFormField
                        name="faqMD"
                        attributes={filePickerAttributes}
                        path={faqMDSrc}
                        isLoading={faqMdLoading}
                    />
                </SettingDisplay>
                <SettingDisplay label="Privacy Markdown">
                    <FileInputFormField
                        name="privacyMD"
                        attributes={filePickerAttributes}
                        path={privacyMDSrc}
                        isLoading={privacyMdLoading}
                    />
                </SettingDisplay>
                <SettingDisplay label="Terms Markdown">
                    <FileInputFormField
                        name="termsMD"
                        attributes={filePickerAttributes}
                        path={termsMDSrc}
                        isLoading={termsMdLoading}
                    />
                </SettingDisplay>
            </Box>
        ),
        [
            aboutMDSrc,
            aboutMdLoading,
            faqMDSrc,
            faqMdLoading,
            privacyMDSrc,
            privacyMdLoading,
            termsMDSrc,
            termsMdLoading
        ]
    );

    const barcodePrefixValue = React.useMemo(() => {
        const type = getBarcodePrefixType(settings?.app?.userBarcodePrefix);
        if (type === EBarcodePrefix.UNSET) {
            return 'Unset';
        }
        if (type === EBarcodePrefix.EMPTY) {
            return 'Empty';
        }
        return settings?.app?.userBarcodePrefix;
    }, [settings?.app?.userBarcodePrefix]);
    return (
        <StyledMuiCardForm
            title="General Settings"
            initialValues={{
                region: settings?.region?.code,
                appUserAuth: !isDefined(settings?.iam?.appUserAuth) ? 'LEGACY' : settings?.iam?.appUserAuth,
                identityProviders: identityProvidersOptions
                    .map(option => option.value)
                    .filter(provider => {
                        if (provider === 'phone' && settings?.phoneAuthEnabled) {
                            return true;
                        }
                        if (provider === 'facebook' && settings?.facebookAuthEnabled) {
                            return true;
                        }
                        if (provider === 'email' && settings?.emailAuthEnabled) {
                            return true;
                        }
                        return false;
                    }),
                uniqueDeviceVerificationEnabled: settings?.uniqueDeviceVerificationEnabled ?? false,
                supportedCountryCallingCodes: supportedCountryCallingCodeOptions.filter(code => {
                    const supported = settings?.phoneAuth?.supportedCountryCallingCodes ?? [
                        settings?.region?.phoneNumberCode
                    ];
                    return supported.includes(code.value);
                }),
                ageRequestEnabled: settings?.app?.ageRequestEnabled ?? false,
                registrationMinimumAge: settings?.app?.registrationMinimumAge ?? '',
                registrationShowMarketingOptIn: settings?.app?.registrationShowMarketingOptIn ?? true,
                offlineCheckinEnabled: settings?.offlineCheckinEnabled ?? false,
                userBarcodeFormat: settings?.app?.userBarcodeFormat ?? EUserBarcodeFormat.PDF417,
                userBarcodePrefix: settings?.app?.userBarcodePrefix,
                userBarcodePrefixType: getBarcodePrefixType(settings?.app?.userBarcodePrefix),
                secondaryCredentialsEnabled: settings?.secondaryCredentialsEnabled ?? false,
                aboutMD: undefined,
                termsMD: undefined,
                faqMD: undefined,
                privacyMD: undefined,
                timezone: settings?.region?.timezone,
                locale: !isDefined(settings?.region?.locale) ? 'en-GB' : settings?.region?.locale,
                taxLabel: settings?.region?.taxLabel,
                inAppReviewPromptsEnabled: !!settings?.inAppReviewPromptsEnabled,
                showCopyright: settings?.app?.showCopyright ?? true
            }}
            onSubmit={onSubmit}
            childrenWhileDisplaying={
                <Box display="flex" flexDirection="column">
                    <SettingDisplayText buildtimeIOS title="Region" text={settings?.region?.code} />
                    <SettingDisplayText
                        title="User Authentication"
                        text={`${getAppUserAuthDisplayText(settings?.iam?.appUserAuth)}`}
                    />
                    <SettingDisplayText title="Timezone" text={settings?.region?.timezone} />
                    <SettingDisplayText
                        title="Locale"
                        text={!isDefined(settings?.region?.locale) ? 'en-GB' : settings.region.locale}
                    />
                    <SettingDisplayText buildtimeIOS title="Tax Label" text={settings?.region?.taxLabel} />
                    <SettingDisplay label="Identity Providers">
                        {settings?.emailAuthEnabled && (
                            <Chip label="Email" color="primary" className={classes.identityChip} />
                        )}
                        {settings?.phoneAuthEnabled && (
                            <Chip label="Phone" color="primary" className={classes.identityChip} />
                        )}
                        {settings?.facebookAuthEnabled && (
                            <Chip label="Facebook" color="primary" className={classes.identityChip} />
                        )}
                    </SettingDisplay>
                    <SettingDisplaySwitch
                        title="Unique Device Verification"
                        description="Should users only be able to sign up for a single account per device"
                        checked={settings?.uniqueDeviceVerificationEnabled}
                    />
                    {renderSupportedCountryCodes()}
                    <SettingDisplaySwitch
                        title="Age Request Enabled"
                        description="Should the App ask the user for their age on sign up?"
                        checked={settings?.app?.ageRequestEnabled}
                    />
                    <SettingDisplayText
                        title="Signup Minimum Age"
                        description="What is the minimum age, if enabled, to sign up to the App?"
                        text={settings?.app?.registrationMinimumAge ?? 'None'}
                    />
                    <SettingDisplaySwitch
                        title="Ask for Marketing on Registration"
                        description="Should the App ask the user for marketing preferences on sign up?"
                        checked={settings?.app?.registrationShowMarketingOptIn ?? true}
                    />
                    <SettingDisplaySwitch title="Offline Checkin" checked={settings?.offlineCheckinEnabled} />
                    <SettingDisplay label="User Barcode Format">
                        <Chip
                            label={getUserBarcodeFormatLabel(settings?.app?.userBarcodeFormat)}
                            color="primary"
                        />
                    </SettingDisplay>
                    <SettingDisplayText title="Barcode Prefix" text={barcodePrefixValue} />
                    <SettingDisplaySwitch
                        title="Additional Credentials"
                        checked={settings?.secondaryCredentialsEnabled}
                    />
                    <SettingDisplaySwitch
                        title="App Store Ratings & Reviews Enabled"
                        checked={!!settings?.inAppReviewPromptsEnabled}
                    />
                    <SettingDisplaySwitch
                        title="Show Pepper Copyright"
                        checked={settings?.app?.showCopyright ?? true}
                    />
                    <SettingDisplayFile
                        title="About Markdown"
                        noFileText="Click Edit to upload About Markdown"
                        url={getAboutUrl()}
                        fileName={aboutMDSrc}
                    />
                    <SettingDisplayFile
                        title="FAQ Markdown"
                        noFileText="Click Edit to change FAQ Markdown"
                        url={getFaqUrl()}
                        fileName={faqMDSrc}
                    />
                    <SettingDisplayFile
                        title="Privacy Markdown"
                        noFileText="Click Edit to change Privacy Markdown"
                        url={getPrivacyUrl()}
                        fileName={privacyMDSrc}
                    />
                    <SettingDisplayFile
                        title="Terms Markdown"
                        noFileText="Click Edit to change Terms Markdown"
                        url={getTermsUrl()}
                        fileName={termsMDSrc}
                    />
                </Box>
            }
            validationSchema={generalSettingsValidationSchema}
            childrenWhileEditing={renderGlobalEdit}
            className={classes.settingsCard}
            boxClassName={classes.displayClassName}
        />
    );
};
